import React, { useCallback } from 'react'
import { Location } from 'history'
import ModalAccept from 'blocks.simple/modal/_accept/modal_accept'
import { RouteLeavingGuard } from 'molecules/CustomPromt'
import translate from 'core/translate'
import { changeLocation } from 'features/routes'
import { isRedirectToLogin, isRoutePathnameChange } from 'features/routes/utils'
import { TemplateEditorContext } from './hooks/templateEditorContext'
import { useTemplateEditor } from './hooks/useTemplateEditor'
import { TemplateEditorToolbar } from './__toolbar/templateEditor__toolbar'
import { TemplateEditorLeftPanels } from './__leftPanels/templateEditor__leftPanels'
import { TemplateEditorCanvas } from './__canvas/templateEditor__canvas'
import { TemplateEditorPlaceCursor } from './__canvas/templateEditor__placeCursor'
import { TemplateEditorInspector } from './__inspector/templateEditor__inspector'
import styles from './templateEditor.jcss'

export const TemplateEditorView = () => {
    const editor = useTemplateEditor()

    const shouldBlockNavigation = useCallback(
        (nextLocation: Location, prevLocation?: Location) =>
            !isRedirectToLogin(nextLocation.pathname) &&
            Boolean(prevLocation && isRoutePathnameChange(nextLocation, prevLocation)) &&
            editor.isDirty,
        [editor.isDirty]
    )

    return (
        <TemplateEditorContext.Provider value={editor}>
            <div className={styles.root}>
                <TemplateEditorToolbar />

                <div className={styles.body}>
                    <TemplateEditorLeftPanels />
                    <TemplateEditorCanvas />
                    <TemplateEditorInspector />
                </div>

                <TemplateEditorPlaceCursor placeMode={editor.placeMode} hidden={Boolean(editor.previewRect)} />

                {editor.editorToast && <div className={styles.toast}>{editor.editorToast}</div>}
            </div>

            <RouteLeavingGuard
                when={editor.isDirty}
                shouldBlockNavigation={shouldBlockNavigation}
                title={translate('notSaved')}
                description={translate('templateEditorUnsavedDescription')}
                acceptLabel=""
                rejectLabel=""
                onAccept={(cb) => {
                    editor.saveTemplate((saved) => cb(saved))
                }}
                navigate={(path) => changeLocation(path)}
            />

            <ModalAccept
                open={editor.discardModalOpen}
                onClose={editor.closeDiscardModal}
                title={translate('notSaved')}
                description={translate('templateEditorUnsavedDescription')}
                acceptLabel={translate('save')}
                rejectLabel={translate('exit')}
                onAccept={editor.confirmDiscardAndSave}
                onReject={editor.confirmDiscardWithoutSave}
            />

            <ModalAccept
                open={Boolean(editor.deleteTarget)}
                onClose={editor.closeDeleteModal}
                title={translate('templateEditorDeleteTemplate')}
                description={
                    editor.deleteTarget ? (
                        <>
                            <div>{editor.deleteTarget.name}</div>
                            <div>{translate('templateEditorDeleteTemplateDescription')}</div>
                        </>
                    ) : null
                }
                acceptLabel={translate('delete')}
                rejectLabel={translate('cancel')}
                onAccept={editor.confirmDeleteTemplate}
                onReject={editor.closeDeleteModal}
            />
        </TemplateEditorContext.Provider>
    )
}
